import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import api from '../lib/api';
import { useI18n } from '../i18n/I18nContext';
import { useAuth, homeFor } from '../context/AuthContext';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { MailCheck, Volleyball, ShieldCheck } from 'lucide-react';

const inputCls = "h-14 rounded-2xl text-base bg-nuit-900 border-nuit-600 text-creme placeholder:text-brume focus:ring-4 focus:ring-or-400/40";

export default function RegisterYouth() {
  const [params] = useSearchParams();
  const [code, setCode] = useState(params.get('code') || '');
  const navigate = useNavigate();
  const { login } = useAuth();
  const [form, setForm] = useState({ name: '', age: '', email: '', parent_email: '' });
  const [step, setStep] = useState('form');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const { t } = useI18n();

  const age = parseInt(form.age, 10);
  const isMinor = !Number.isNaN(age) && age < 18;

  const submit = async (e) => {
    e.preventDefault();
    if (isMinor && !form.parent_email.includes('@')) return;
    setLoading(true);
    try {
      await api.post('/auth/register-youth', {
        name: form.name, age, email: form.email, code,
        parent_email: isMinor ? form.parent_email : null,
      });
      setStep('otp');
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post('/auth/register-youth/verify', { email: form.email, code: otp.trim() });
      if (data.token) {
        login(data.token, data.user);
        navigate(homeFor('jeune'));
      } else {
        setDone(true);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const brand = (
    <div className="flex items-center justify-between gap-2 mb-8">
      <div className="flex items-center gap-2 font-fredoka font-semibold text-lg text-or-400">
        <span className="w-9 h-9 rounded-full bg-or-400 flex items-center justify-center">
          <Volleyball className="w-5 h-5 text-nuit-950" strokeWidth={2.5} />
        </span>
        {t('registerYouth.title')}
      </div>
      <LanguageSwitcher />
    </div>
  );

  if (done) {
    return (
      <div className="min-h-screen bg-nuit-900 flex items-center justify-center px-4">
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
          className="bg-nuit-800 border border-nuit-700 rounded-3xl p-10 max-w-md w-full text-center gold-glow"
          data-testid="register-youth-success">
          <span className="w-16 h-16 rounded-full bg-or-400/15 flex items-center justify-center mx-auto mb-6">
            <MailCheck className="w-8 h-8 text-or-400" strokeWidth={2.5} />
          </span>
          <h1 className="text-2xl sm:text-3xl font-bold text-creme mb-4">{t('registerYouth.successTitle')}</h1>
          <p className="text-base text-brume leading-relaxed">
            {t('registerYouth.successText', { email: form.parent_email })}
          </p>
          <Link to="/" data-testid="back-home-link">
            <Button className="mt-8 rounded-full bg-or-400 hover:bg-or-300 text-nuit-950 font-bold px-8">
              {t('common.backHome')}
            </Button>
          </Link>
        </motion.div>
      </div>
    );
  }

  if (step === 'otp') {
    return (
      <div className="min-h-screen bg-nuit-900 flex items-center justify-center px-4 py-12">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
          className="bg-nuit-800 border border-nuit-700 rounded-3xl p-8 sm:p-10 max-w-md w-full gold-glow"
          data-testid="register-youth-verify-step">
          {brand}
          <div className="text-center mb-6">
            <span className="w-16 h-16 rounded-full bg-or-400/15 flex items-center justify-center mx-auto mb-4">
              <ShieldCheck className="w-8 h-8 text-or-400" strokeWidth={2.5} />
            </span>
            <h1 className="text-2xl font-bold text-creme mb-2">{t('registerYouth.otpTitle')}</h1>
            <p className="text-sm text-brume leading-relaxed">{t('login.codeSentTo', { email: form.email })}</p>
          </div>
          <form onSubmit={verify} className="space-y-5">
            <div>
              <label className="block text-sm font-bold text-creme mb-2" htmlFor="youth-otp">{t('register.otpLabel')}</label>
              <Input id="youth-otp" data-testid="youth-otp-input" required inputMode="numeric" maxLength={6}
                value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))} placeholder="••••••"
                className={`${inputCls} text-center text-2xl tracking-[0.5em] font-bold`} />
            </div>
            <Button type="submit" data-testid="youth-otp-submit" disabled={loading || otp.length !== 6}
              className="w-full h-14 rounded-full bg-or-400 hover:bg-or-300 text-nuit-950 font-bold text-base shadow-md hover:-translate-y-0.5 transition-transform">
              {loading ? t('common.sending') : t('register.otpSubmit')}
            </Button>
            <p className="text-center text-sm text-brume">
              <button type="button" onClick={() => setStep('form')} data-testid="youth-otp-back"
                className="text-or-400 font-bold hover:underline">{t('register.otpBack')}</button>
            </p>
          </form>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-nuit-900 flex items-center justify-center px-4 py-12">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
        className="bg-nuit-800 border border-nuit-700 rounded-3xl p-8 sm:p-10 max-w-md w-full gold-glow"
        data-testid="register-youth-page">
        {brand}
        <form onSubmit={submit} className="space-y-5">
          <div>
            <label className="block text-sm font-bold text-creme mb-2" htmlFor="youth-code">{t('register.codeLabel')}</label>
            <Input id="youth-code" data-testid="youth-code-input" required value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder={t('landing.codePlaceholder')}
              className={`${inputCls} font-mono tracking-widest`} />
          </div>
          <div>
            <label className="block text-sm font-bold text-creme mb-2" htmlFor="youth-name">{t('registerYouth.name')}</label>
            <Input id="youth-name" data-testid="youth-name-input" required value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t('registerYouth.namePlaceholder')}
              className={inputCls} />
          </div>
          <div>
            <label className="block text-sm font-bold text-creme mb-2" htmlFor="youth-age">{t('registerYouth.age')}</label>
            <Input id="youth-age" data-testid="youth-age-input" required type="number" min="4" max="99" value={form.age}
              onChange={(e) => setForm({ ...form, age: e.target.value })} placeholder={t('registerYouth.agePlaceholder')}
              className={inputCls} />
          </div>
          <div>
            <label className="block text-sm font-bold text-creme mb-2" htmlFor="youth-email">{t('login.email')}</label>
            <Input id="youth-email" data-testid="youth-email-input" required type="email" value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder={t('login.emailPlaceholder')}
              className={inputCls} />
          </div>
          {isMinor && (
            <div data-testid="youth-parent-block">
              <label className="block text-sm font-bold text-creme mb-2" htmlFor="parent-email">{t('registerYouth.parentEmail')}</label>
              <Input id="parent-email" data-testid="parent-email-input" required type="email" value={form.parent_email}
                onChange={(e) => setForm({ ...form, parent_email: e.target.value })} placeholder={t('registerYouth.parentEmailPlaceholder')}
                className={inputCls} />
            </div>
          )}
          <p className="text-xs text-brume leading-relaxed" data-testid="youth-note">
            {Number.isNaN(age) ? t('registerYouth.noteNeutral') : isMinor ? t('registerYouth.note') : t('registerYouth.noteAdult')}
          </p>
          <Button type="submit" data-testid="register-youth-submit"
            disabled={loading || !code.trim() || !form.name.trim() || Number.isNaN(age) || !form.email.includes('@') || (isMinor && !form.parent_email.includes('@'))}
            className="w-full h-14 rounded-full bg-or-400 hover:bg-or-300 text-nuit-950 font-bold text-base shadow-md hover:-translate-y-0.5 transition-transform">
            {loading ? t('common.sending') : isMinor ? t('registerYouth.submit') : t('registerAdult.submit')}
          </Button>
        </form>
      </motion.div>
    </div>
  );
}
